import { ASSET_MATCH_CHOICES_MAX } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon, Input, RadioGroup, Skeleton } from "@sagot/ui";
import { useState } from "react";
import { useDebounced } from "../../../lib/slug";
import { useWorkspaceId } from "../../../session/workspace-context";
import { serviceKeys } from "../service-keys";
import { useServiceQuery } from "../use-service-query";

const NOT_LISTED = "__not_listed__";
const SEARCH_DEBOUNCE_MS = 300;

interface AssetPickerProps {
  /** The requester's organisation: only its own equipment is offered (another organisation's is never shown). */
  organisationId: string | null;
  /** The chosen asset id, or "" for none / "Not listed". */
  value: string;
  onChange: (assetId: string) => void;
  /** Accessible name of the group (the field's label). */
  label: string;
}

/**
 * Pick the equipment a ticket is about (AST-005, AST-051): at most six of the organisation's own assets, newest first, and
 * "Not listed" for equipment that is not registered. Typing narrows the list through the server; nothing is guessed.
 */
export function AssetPicker({ organisationId, value, onChange, label }: AssetPickerProps) {
  const { t } = useTranslation();
  const ws = useWorkspaceId();
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim(), SEARCH_DEBOUNCE_MS);
  const assets = useServiceQuery({
    queryKey: serviceKeys.assetLookups(ws, "ticket-picker", { organisationId, q }),
    enabled: organisationId !== null,
    staleTime: 15_000,
    request: (api, signal) => api.GET("/v1/assets", { params: { query: { organisation_id: organisationId, q: q || null, limit: ASSET_MATCH_CHOICES_MAX } }, signal }),
  });
  if (organisationId === null) return <p className="text-xs text-fg-muted" data-testid="asset-picker-needs-org">{t("service.tickets.new.assetPicker.needsOrganisation")}</p>;
  const items = assets.data?.items ?? [];
  const total = assets.data?.total ?? items.length;
  return (
    <div className="space-y-2" data-testid="asset-picker">
      <div className="relative">
        <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
        <Input className="pl-9" type="search" autoComplete="off" value={query} placeholder={t("service.tickets.new.assetPicker.search")} aria-label={t("service.tickets.new.assetPicker.searchLabel")} onChange={(event) => { setQuery(event.target.value); }} />
      </div>
      {assets.isPending ? <Skeleton className="h-20 w-full" /> : null}
      {assets.isError ? <p role="alert" className="text-xs text-danger">{t("service.tickets.new.assetPicker.error")}</p> : null}
      {assets.isSuccess ? (
        <RadioGroup
          aria-label={label}
          value={value === "" ? NOT_LISTED : value}
          onValueChange={(next) => { onChange(next === NOT_LISTED ? "" : next); }}
          options={[
            ...items.map((asset) => ({ value: asset.id, label: <><span className="font-mono text-xs font-bold">{asset.tag}</span> {asset.name}</>, description: asset.serial_number ?? undefined })),
            { value: NOT_LISTED, label: t("service.tickets.new.assetPicker.notListed") },
          ]}
        />
      ) : null}
      {assets.isSuccess && total > items.length ? <p className="text-xs text-fg-subtle">{t("service.tickets.new.assetPicker.narrow", { shown: items.length, total })}</p> : null}
    </div>
  );
}
